import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import DirectoryConfirmDialog from './DirectoryConfirmDialog'
import {
  Pager,
  RoleBadge,
  Section,
  TableScroller,
  ToolbarInput,
  ToolbarSelect,
  fetchJSON,
  useBearer,
  useDebouncedValue,
  usePagedQuery,
  useToast,
} from './_admin-utils'
import { useAuthContext } from '@/lib/auth-context'
import UserAllowlistControls from '@/components/admin/UserAllowlistControls'

interface User {
  id: string
  full_name?: string
  email?: string
  role?: string
  created_at?: string
  disabled_at?: string | null
}

interface ConfirmState {
  type: 'disable' | 'delete'
  user: User
  busy: boolean
}

interface ActionMenuProps {
  disableDisabled: boolean
  deleteDisabled: boolean
  onDisable: () => void
  onDelete: () => void
  onAllowlist?: () => void
}

interface FetcherParams {
  page: number
  pageSize: number
  signal?: AbortSignal
}

const ROLE_FILTERS = [
  { value: '', label: 'All roles' },
  { value: 'teacher', label: 'Teachers' },
  { value: 'sponsor', label: 'Sponsors' },
  { value: 'coach', label: 'Coaches' },
  { value: 'guardian', label: 'Guardians' },
  { value: 'admin', label: 'Admins' },
  { value: 'superadmin', label: 'Superadmins' },
]

const DEFAULT_PAGE_SIZE = 20

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch (error) {
    return '—'
  }
}

function ActionMenu({ disableDisabled, deleteDisabled, onDisable, onDelete, onAllowlist }: ActionMenuProps) {
  const [open, setOpen] = useState(false)

  const close = useCallback(() => setOpen(false), [])

  return (
    <details
      className="relative"
      open={open}
      onToggle={(event) => setOpen((event.target as HTMLDetailsElement).open)}
    >
      <summary className="inline-flex cursor-pointer list-none select-none">
        <span className="inline-flex items-center rounded-full border border-cocoa/20 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell/60">
          Actions
        </span>
      </summary>
      <div className="absolute right-0 z-10 mt-2 w-44 rounded-xl border border-white/80 bg-white p-1.5 text-sm shadow-lg">
        {onAllowlist ? (
          <button
            type="button"
            className="w-full rounded-lg px-3 py-2 text-left text-cocoa hover:bg-shell/60"
            onClick={() => {
              close()
              onAllowlist()
            }}
          >
            Manage allowlist
          </button>
        ) : null}
        <button
          type="button"
          className="w-full rounded-lg px-3 py-2 text-left text-cocoa hover:bg-shell/60 disabled:opacity-40 disabled:hover:bg-transparent"
          onClick={() => {
            close()
            onDisable()
          }}
          disabled={disableDisabled}
        >
          Disable user
        </button>
        <button
          type="button"
          className="w-full rounded-lg px-3 py-2 text-left text-terracotta hover:bg-terracotta/10 disabled:opacity-40 disabled:hover:bg-transparent"
          onClick={() => {
            close()
            onDelete()
          }}
          disabled={deleteDisabled}
        >
          Delete user…
        </button>
      </div>
    </details>
  )
}

export default function UsersDirectory() {
  const bearer = useBearer()
  const { profile } = useAuthContext()
  const { show, ToastHost } = useToast()
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query.trim(), 300)
  const [role, setRole] = useState('')
  const [confirm, setConfirm] = useState<ConfirmState | null>(null)
  const [allowlistUser, setAllowlistUser] = useState<User | null>(null)

  const fetcher = useCallback(
    async ({ page, pageSize, signal }: FetcherParams): Promise<{ items: User[]; total: number }> => {
      if (!bearer) throw new Error('Not authenticated')

      const params = new URLSearchParams()
      if (debouncedQuery) params.set('q', debouncedQuery)
      if (role) params.set('role', role)
      params.set('page', String(page))
      params.set('pageSize', String(pageSize))

      const data = await fetchJSON(`/api/v1/admin/users?${params.toString()}`, {
        bearer,
        signal,
      }) as { data?: { items?: User[]; total?: number }; items?: User[]; total?: number } | null

      return {
        items: Array.isArray(data?.data?.items) ? data.data.items : Array.isArray(data?.items) ? data.items : [],
        total: typeof data?.data?.total === 'number' ? data.data.total : typeof data?.total === 'number' ? data.total : 0,
      }
    },
    [bearer, debouncedQuery, role],
  )

  const { items, setItems, page, pageSize, pageCount, loading, error, load } = usePagedQuery(
    fetcher,
    [fetcher],
    DEFAULT_PAGE_SIZE,
  )

  const handleConfirm = async () => {
    if (!confirm) return
    const { type, user } = confirm
    setConfirm((prev) => (prev ? { ...prev, busy: true } : prev))

    try {
      if (type === 'disable') {
        await fetchJSON('/api/v1/admin/users/delete', {
          bearer,
          method: 'POST',
          body: { user_id: user.id, hard: false },
        })
        setItems((prev) => prev.filter((item) => item.id !== user.id))
        show('User disabled')
      } else if (type === 'delete') {
        await fetchJSON('/api/v1/admin/users/delete', {
          bearer,
          method: 'POST',
          body: { user_id: user.id, hard: true },
        })
        setItems((prev) => prev.filter((item) => item.id !== user.id))
        show('User deleted')
      }

      await load(page)
    } catch (actionError: unknown) {
      const err = actionError as Error | null
      if (err?.name === 'AbortError') return
      show(err?.message || 'Unable to complete action')
    } finally {
      setConfirm(null)
    }
  }

  const confirmTitle = confirm
    ? confirm.type === 'delete'
      ? 'Delete user'
      : 'Disable user'
    : ''

  const confirmDescription = confirm
    ? confirm.type === 'delete'
      ? 'This removes the user profile and their authentication record permanently. This action cannot be undone.'
      : 'Disabled users are immediately signed out and can no longer access the portal until re-enabled by an admin.'
    : ''

  return (
    <Section
      title="Users Directory"
      actions={
        <Link
          to="/admin/create-user"
          className="rounded-full border border-cocoa/20 bg-white px-4 py-1.5 text-sm font-semibold text-cocoa hover:bg-shell/60"
        >
          Create user
        </Link>
      }
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
          <div className="w-full md:w-64">
            <ToolbarInput value={query} onChange={setQuery} placeholder="Search by name or email…" />
          </div>
          <div className="w-full md:w-48">
            <ToolbarSelect value={role} onChange={setRole} options={ROLE_FILTERS} />
          </div>
        </div>
        <Pager page={page} pageCount={pageCount} onPage={(next) => load(next, pageSize)} />
      </div>

      <TableScroller minWidth={840}>
        <table className="w-full table-fixed border-separate border-spacing-y-2">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-cocoa/60">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((user) => {
              const isSelf = profile?.id === user.id
              const isSuper = user.role === 'superadmin'

              return (
                <tr key={user.id} className="rounded-xl bg-white text-sm shadow-sm">
                  <td className="px-3 py-3 text-sm text-cocoa">
                    <div className="font-medium text-cocoa">{user.full_name || '—'}</div>
                    {user.disabled_at ? (
                      <div className="text-xs text-terracotta">Disabled {formatDateTime(user.disabled_at)}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-3 text-sm text-cocoa/80">{user.email || '—'}</td>
                  <td className="px-3 py-3 text-sm text-cocoa/80">
                    <RoleBadge role={user.role} />
                  </td>
                  <td className="px-3 py-3 text-sm text-cocoa/80">{formatDateTime(user.created_at)}</td>
                  <td className="px-3 py-3 text-right">
                    <ActionMenu
                      disableDisabled={isSelf || isSuper}
                      deleteDisabled={isSelf || isSuper}
                      onDisable={() => setConfirm({ type: 'disable', user, busy: false })}
                      onDelete={() => setConfirm({ type: 'delete', user, busy: false })}
                      onAllowlist={() => setAllowlistUser(user)}
                    />
                  </td>
                </tr>
              )
            })}
            {!loading && items.length === 0 ? (
              <tr>
                <td className="px-3 py-6 text-center text-sm text-cocoa/60" colSpan={5}>
                  No users found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </TableScroller>

      {loading ? <p className="mt-4 text-sm text-cocoa/60">Loading users…</p> : null}
      {error ? (
        <p className="mt-4 rounded-lg border border-terracotta/40 bg-terracotta/10 p-3 text-sm text-terracotta">{error}</p>
      ) : null}

      <DirectoryConfirmDialog
        open={Boolean(confirm)}
        title={confirmTitle}
        description={confirmDescription}
        confirmLabel={confirm?.type === 'delete' ? 'Delete user' : 'Disable user'}
        confirmTone={confirm?.type === 'delete' ? 'danger' : 'default'}
        busy={Boolean(confirm?.busy)}
        onCancel={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />

      {allowlistUser ? (
        <div className="mt-6 rounded-2xl border border-sand/60 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-cocoa">Allowlist roles</h2>
              <p className="text-sm text-cocoa/70">
                Manage access for <span className="font-medium text-cocoa">{allowlistUser.email || '—'}</span>
              </p>
            </div>
            <button
              type="button"
              className="text-sm text-cocoa/70 underline"
              onClick={() => setAllowlistUser(null)}
            >
              Close
            </button>
          </div>
          <UserAllowlistControls email={allowlistUser.email} bearer={bearer || undefined} />
        </div>
      ) : null}

      <ToastHost />
    </Section>
  )
}
